import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { notifications as notifApi } from "../services/api";
import { ArrowLeft, MessageCircle, Loader, Eye, MapPin } from "lucide-react";
import { thumbnailUrl as optimizedThumb } from "../utils/cloudinary";
import { useTranslation } from "react-i18next";

function Notifications() {
  const { token, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t("notifications.title")} | Pinned`;
    return () => { document.title = t("app.title"); };
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated) return;
    async function load() {
      try {
        const data = await notifApi.getAll(token);
        setNotifs(data);
        // Mark all as read on open
        await notifApi.markRead(token);
      } catch {
        setNotifs([]);
      }
      setLoading(false);
    }
    load();
  }, [token, isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="h-full flex items-center justify-center bg-th-bg">
        <Link to="/login" className="text-sm text-th-accent">{t("notifications.loginPrompt")}</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-th-bg">
        <Loader size={20} className="animate-spin text-th-accent" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
      <div className="max-w-xl mx-auto">
        <div className="px-4 pt-[max(1rem,env(safe-area-inset-top))] mt-2 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 bg-th-surface border border-th-border text-th-muted rounded-xl flex items-center justify-center"
            >
              <ArrowLeft size={16} />
            </button>
            <h1 className="text-lg font-bold text-th-text">{t("notifications.title")}</h1>
          </div>

          {notifs.length === 0 ? (
            <p className="text-center text-sm text-th-muted py-12">{t("notifications.empty")}</p>
          ) : (
            <div className="space-y-1">
              {notifs.map((n) => {
                const linkTo = n.type === "welcome"
                  ? `/profile/${n.actor?.username || "wheresandres"}`
                  : n.postId
                    ? `/profile/${user?.username || user?.id}/post/${n.postId}`
                    : "/profile";

                const { icon: Icon, iconColor, bgColor, borderColor } = notifStyle(n.type);

                return (
                  <Link
                    key={n.id}
                    to={linkTo}
                    className={`flex items-start gap-3 px-3 py-3 rounded-xl transition-colors ${
                      n.read ? "bg-th-bg" : "bg-th-surface"
                    }`}
                  >
                    {n.type === "welcome" && n.actor?.avatarUrl ? (
                      <img
                        src={optimizedThumb(n.actor.avatarUrl)}
                        alt=""
                        className="w-8 h-8 rounded-lg object-cover flex-shrink-0 mt-0.5 border border-th-border"
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded-lg ${bgColor} border ${borderColor} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon size={14} className={iconColor} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-th-text">
                        {n.type === "welcome" ? (
                          <>
                            <span className="font-semibold">@{n.actor?.username || "wheresandres"}</span>
                            {" "}{t("notifications.welcome")}
                          </>
                        ) : n.type === "view_milestone" ? (
                          <>
                            {t("notifications.viewMilestone")}
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">@{n.actor?.username || t("notifications.someone")}</span>
                            {" "}{t("post.commentedOnPost")}
                          </>
                        )}
                      </p>
                      {n.post && (
                        <p className="text-xs text-th-muted mt-0.5 truncate">{n.post.caption}</p>
                      )}
                      <p className="text-[11px] text-th-muted mt-1 font-mono">
                        {timeAgo(n.createdAt, t)}
                      </p>
                    </div>
                    {n.post?.thumbnailUrl && (
                      <img
                        src={optimizedThumb(n.post.thumbnailUrl)}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-th-border"
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function notifStyle(type) {
  switch (type) {
    case "welcome":
      return { icon: MapPin, iconColor: "text-emerald-500", bgColor: "bg-emerald-500/10", borderColor: "border-emerald-500/30" };
    case "view_milestone":
      return { icon: Eye, iconColor: "text-blue-400", bgColor: "bg-blue-400/10", borderColor: "border-blue-400/30" };
    default:
      return { icon: MessageCircle, iconColor: "text-th-accent", bgColor: "bg-th-accent-dim", borderColor: "border-th-accent-border" };
  }
}

function timeAgo(dateStr, t) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("time.justNow");
  if (mins < 60) return t("time.mAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("time.hAgo", { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t("time.dAgo", { count: days });
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default Notifications;
